// src/components/ui/NativeSheet.jsx
// Bottom sheet nativo (iOS/Apple) en móvil · Dialog centrado en desktop
// Ultra rápido · GPU · Portal · Scroll lock · Escape

import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { usePortal, useBodyScrollLock, useEscapeKey, portalize } from "../../utils/portalUtils";

const springSheet = {
  type: "spring",
  stiffness: 420,
  damping: 36,
  mass: 0.85,
};

/**
 * NativeSheet — modal/sheet reutilizable nivel Apple / Stripe / Linear
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} title
 * @param {React.ReactNode} children
 * @param {React.ReactNode} footer
 * @param {React.ReactNode} headerRight
 * @param {'default'|'danger'|'success'} tone
 * @param {string} maxWidth - desktop max width class
 * @param {boolean} showHandle - grabber en móvil
 * @param {string} className
 */
export default function NativeSheet({
  open,
  onClose,
  title,
  children,
  footer,
  headerRight,
  tone = "default",
  maxWidth = "max-w-md",
  showHandle = true,
  className = "",
  fullHeightMobile = false,
}) {
  const isMobile = useIsMobile();
  const mounted = usePortal();
  const sheetRef = useRef(null);

  useBodyScrollLock(open);
  useEscapeKey(open, onClose);

  // Focus trap ligero
  useEffect(() => {
    if (!open || !sheetRef.current) return;
    const el = sheetRef.current.querySelector(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
    );
    if (el) setTimeout(() => el.focus?.({ preventScroll: true }), 80);
  }, [open]);

  if (!mounted) return null;

  const toneAccent =
    tone === "danger"
      ? "text-red-600 dark:text-red-400"
      : tone === "success"
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-primary";

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          key="sheet-root"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className={`fixed inset-0 z-[99999] flex ${
            isMobile ? "items-end justify-center" : "items-center justify-center p-4"
          }`}
          role="dialog"
          aria-modal="true"
          aria-label={title || "Dialog"}
        >
          {/* Backdrop */}
          <motion.button
            type="button"
            aria-label="Fechar"
            className="absolute inset-0 bg-black/45 dark:bg-black/65 backdrop-blur-[3px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            ref={sheetRef}
            initial={
              isMobile
                ? { y: "100%", opacity: 0.96 }
                : { scale: 0.94, opacity: 0, y: 8 }
            }
            animate={isMobile ? { y: 0, opacity: 1 } : { scale: 1, opacity: 1, y: 0 }}
            exit={
              isMobile
                ? { y: "100%", opacity: 0.96 }
                : { scale: 0.96, opacity: 0, y: 6 }
            }
            transition={isMobile ? springSheet : { duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className={`relative z-10 w-full ${maxWidth} bg-modal border border-color shadow-2xl overflow-hidden flex flex-col ${
              isMobile
                ? `rounded-t-[22px] ${fullHeightMobile ? "max-h-[92dvh]" : "max-h-[88dvh]"}`
                : "rounded-2xl max-h-[90vh]"
            } ${className}`}
            style={{
              paddingBottom: isMobile ? "max(12px, env(safe-area-inset-bottom))" : undefined,
              willChange: "transform, opacity",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle iOS */}
            {isMobile && showHandle && (
              <div className="flex justify-center pt-2.5 pb-1 flex-shrink-0">
                <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
              </div>
            )}

            {/* Header */}
            {(title || headerRight) && (
              <div
                className={`flex items-center justify-between gap-3 flex-shrink-0 border-b border-color ${
                  isMobile ? "px-4 pb-3 pt-1" : "px-5 py-4"
                }`}
              >
                <h2 className={`font-bold ${toneAccent} ${isMobile ? "text-base" : "text-lg"} truncate`}>
                  {title}
                </h2>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {headerRight}
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-2 rounded-xl text-muted hover:bg-hover active:scale-95 transition touch-feedback"
                    aria-label="Fechar"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* Body */}
            <div
              className={`flex-1 overflow-y-auto overscroll-contain ${
                isMobile ? "px-4 py-3" : "px-5 py-4"
              }`}
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {children}
            </div>

            {/* Footer sticky */}
            {footer && (
              <div
                className={`flex-shrink-0 border-t border-color bg-modal ${
                  isMobile ? "px-4 py-3" : "px-5 py-4"
                }`}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return portalize(mounted, content);
}
